import { createContext, ReactNode, useCallback, useLayoutEffect, useRef, useState } from 'react'

import { css } from '@emotion/react'
import styled from '@emotion/styled'
import FocusLock from 'react-focus-lock'

export type DatePickerDialogProps = {
  open: boolean
  onClose?: () => void
  renderTarget: (targetProps: { 'aria-haspopup': 'dialog' }) => ReactNode
  renderPicker: () => ReactNode
}

const RelativeContainer = styled.div`
  position: relative;
`

type Direction = 'top' | 'bottom'

const DialogAnchor = styled.div<{ direction: Direction }>`
  position: absolute;
  z-index: 1;
  ${props =>
    props.direction === 'top'
      ? css`
          top: calc(-1 * ${props.theme.spacing.default});
        `
      : css`
          bottom: calc(-1 * ${props.theme.spacing.default});
        `}
`

const Dialog = styled.div<{ direction: Direction }>`
  position: absolute;

  ${props =>
    props.direction === 'top' &&
    css`
      transform: translateY(-100%);
    `}
`

const useDialogDirection = (open: boolean) => {
  const dialog = useRef<HTMLDivElement>(null)
  const [direction, setDirection] = useState<Direction>('bottom')

  useLayoutEffect(() => {
    if (open && dialog.current) {
      setDirection(dialog.current.getBoundingClientRect().bottom > window.innerHeight ? 'top' : 'bottom')
    } else {
      setDirection('bottom')
    }
  }, [open])

  return { dialog, direction }
}

export const DialogFocusContext = createContext({ returnFocus: (): void => undefined })

export const DatePickerDialog = ({ open, onClose, renderTarget, renderPicker }: DatePickerDialogProps) => {
  const { dialog, direction } = useDialogDirection(open)
  const [focusInitiator, setFocusInitiator] = useState<HTMLDivElement | null>(null)

  const returnFocus = useCallback(() => focusInitiator?.focus({ preventScroll: true }), [focusInitiator])

  return (
    <DialogFocusContext.Provider value={{ returnFocus }}>
      <RelativeContainer
        onFocus={event => {
          if (!open) {
            setFocusInitiator(event.target)
          }
        }}
        onKeyDown={event => {
          if (open && event.key === 'Escape') {
            event.stopPropagation()
            onClose?.()
            returnFocus()
          }
        }}
      >
        <FocusLock returnFocus={false} disabled={!open} data-testid="date-picker-focus-lock">
          {renderTarget({ 'aria-haspopup': 'dialog' })}
          {open && (
            <DialogAnchor direction={direction}>
              <Dialog ref={dialog} direction={direction} role="dialog">
                {renderPicker()}
              </Dialog>
            </DialogAnchor>
          )}
        </FocusLock>
      </RelativeContainer>
    </DialogFocusContext.Provider>
  )
}
